import type { CatalogGroup } from "./types";

/**
 * Los valores de un borrador **con el tipo de las opciones del select** (xcons-docs#474).
 *
 * El importador guarda la opción como texto (`"5665"`) y el esquema la ofrece como número
 * (`5665`): el `Select` de antd compara con `===`, no la encontraba y mostraba el id suelto
 * en Marca, Unidad de venta, Impuestos y Tipo de envío. Un multiselect importado llega como
 * `"1,2"`. Se convierte al abrir, así que vale también para los borradores que ya existen.
 *
 * Sólo se toca un atributo con lista y un valor que es id de una opción; lo demás pasa igual.
 */
export const valoresConTipoDeOpcion = (
  valores: Record<string, unknown>,
  grupos: CatalogGroup[],
): Record<string, unknown> => {
  const salida: Record<string, unknown> = { ...valores };
  for (const atributo of grupos.flatMap((g) => g.attributes)) {
    const opciones = atributo.options;
    const valor = salida[atributo.code];
    if (
      !opciones?.length ||
      valor === null ||
      valor === undefined ||
      valor === ""
    ) {
      continue;
    }
    const numericas = typeof opciones[0].value === "number";
    const convertir = (v: unknown) => {
      const texto = String(v).trim();
      return numericas && /^\d+$/.test(texto) ? Number(texto) : v;
    };
    if (atributo.frontendInput === "multiselect") {
      const lista = Array.isArray(valor)
        ? valor
        : String(valor)
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);
      salida[atributo.code] = lista.map(convertir);
    } else if (!Array.isArray(valor)) {
      salida[atributo.code] = convertir(valor);
    }
  }
  return salida;
};

/** Lo que un producto del seller le pasa al alta cuando se cataloga desde el asociador. */
export interface PrecargaDelSeller {
  name?: string;
  manufacturer?: string;
  price?: number;
  manufacturerSku?: string;
}

/** De dónde vino un alta de + Catalogar: el asociador, con su URL y su cola, para volver. */
export interface DestinoDelAsociador {
  ruta: string;
  state?: unknown;
}

/**
 * El alta precargada desde **+ Catalogar** del asociador (xcons-docs#609): el seller subió su
 * producto con pocos datos y el borrador arranca con esos, no en blanco.
 *
 * La marca del seller es **texto** y la del catálogo una opción: sólo se precarga si coincide
 * con una etiqueta, sin distinguir mayúsculas. Si no, queda vacía — elegir una marca parecida
 * armaría el SKU con la equivocada, y el SKU se fija al primer guardado.
 */
export const valoresPrecargados = (
  precarga: PrecargaDelSeller | null | undefined,
  grupos: CatalogGroup[],
): Record<string, unknown> => {
  if (!precarga) return {};
  const salida: Record<string, unknown> = {};
  if (precarga.name?.trim()) salida.name = precarga.name.trim();
  if (typeof precarga.price === "number" && precarga.price > 0) salida.price = precarga.price;
  if (precarga.manufacturerSku?.trim()) salida.skuFabricante = precarga.manufacturerSku.trim();

  const marca = precarga.manufacturer?.trim().toLowerCase();
  if (marca) {
    const opcion = grupos
      .flatMap((g) => g.attributes)
      .find((a) => a.code === "manufacturer")
      ?.options?.find((o) => String(o.label).trim().toLowerCase() === marca);
    if (opcion) salida.manufacturer = opcion.value;
  }
  return salida;
};

/**
 * El código del fabricante que algunos sellers ponen entre paréntesis al final del nombre:
 * "ALIMENTADOR LED 12V/0-40W (833.74.962)" → "833.74.962". Sólo con un dígito y desde 5
 * caracteres, para no tomar un "(251)" de medida o un "(x10)". Es una sugerencia: el alta lo
 * muestra en SKU Fabricante y la persona lo confirma.
 */
export const codigoEntreParentesis = (nombre?: string): string | undefined => {
  const m = /\(\s*([A-Za-z0-9][A-Za-z0-9./-]{3,})\s*\)\s*$/.exec(nombre ?? "");
  return m && /\d/.test(m[1]) ? m[1] : undefined;
};
